// src/layout/builders/ProfileDetails/SocialMedia.jsx
import { useState, useEffect } from "react";
import useProfileStore from "../../../store/builders/useProfileStore";
import { toast } from "react-hot-toast"; // Import toast

// Updated fields configuration - YouTube removed
const fields = [
  { key: "facebook_url", label: "Facebook URL" },
  { key: "linkedin_url", label: "LinkedIn URL" },
  { key: "instagram_url", label: "Instagram URL" },
];

// Basic URL validation regex (simplified)
const URL_REGEX = /^(ftp|http|https):\/\/[^ "]+$/;

const SocialMedia = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const { profile, fetchProfile, updateProfile, isLoading, isUpdating } = // Added isUpdating
    useProfileStore();

  const loadProfile = async () => {
    await fetchProfile();
  };

  useEffect(() => {
    if (!profile) {
      loadProfile();
    }
  }, [profile, fetchProfile]);

  useEffect(() => {
    if (profile?.builder && !isEditing) {
      const builderData = profile.builder;
      const initialFormData = {};
      fields.forEach((field) => {
        initialFormData[field.key] = builderData[field.key] || "";
      });
      setFormData(initialFormData);
    } else if (!isEditing) {
      const initialFormData = {};
      fields.forEach((field) => {
        initialFormData[field.key] = "";
      });
      setFormData(initialFormData);
    }
  }, [profile, isEditing]);

  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const handleInputChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleCoverImageChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const maxFileSize = 5 * 1024 * 1024;
      const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
      if (file.size > maxFileSize) {
        toast.error("Cover image is too large. Maximum size is 5MB.");
        return;
      }
      if (!allowedTypes.includes(file.type)) {
        toast.error("Invalid file type. Please select a JPG, PNG, or WEBP image.");
        return;
      }
      const success = await updateProfile({ coverImage: file });
      if (success) await fetchProfile();
    }
  };

  const validateForm = () => {
    for (const field of fields) {
      const value = formData[field.key];
      if (value && !URL_REGEX.test(value)) {
        toast.error(`Please enter a valid URL for ${field.label}.`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      // Ensure only defined social media fields are sent in the payload
      const payload = {};
      fields.forEach(field => {
        payload[field.key] = formData[field.key] || ""; // Send empty string if empty
      });

      const success = await updateProfile(payload);
      if (success) {
        // Only toggle edit mode if update was successful
        toggleEdit();
      }
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  if (isLoading && !profile) {
    return (
      <div className="flex justify-center items-center py-6">
        <div className="w-6 h-6 border-2 border-blue-500 border-dashed rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="py-2 text-xs">
      {/* Header */}
      <div className="flex items-center justify-start gap-4 mb-4 border-b border-gray-400 pb-3">
        <h2 className="text-sm font-semibold">Social Media</h2>
        <div className="flex items-center gap-2">
          {isEditing && (
            <button
              className="px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSubmit}
              disabled={isUpdating}
            >
              {isUpdating ? "Saving..." : "Save Changes"}
            </button>
          )}
          <button
            className="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-xs transition-colors"
            onClick={toggleEdit}
            disabled={isUpdating}
          >
            {isEditing ? (isUpdating ? "Saving..." : "Cancel") : "Edit"}
          </button>
        </div>
      </div>

      {/* Social Media Fields */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {fields.map(({ key, label }) => ( // Index removed as it's not used
          <div key={key} className="space-y-1">
            <label className={`block text-xs font-medium ${!formData[key] ? "text-red-500" : "text-gray-600"}`}>
              {label}
            </label>
            <input
              type="url"
              value={formData[key] || ""}
              onChange={(e) => handleInputChange(key, e.target.value)}
              disabled={!isEditing || isUpdating}
              className={`w-full p-2.5 rounded-md text-xs transition-colors ${isEditing
                ? !formData[key]
                  ? "border border-red-500 bg-white focus:ring-1 focus:ring-red-100"
                  : "border border-gray-300 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                : !formData[key]
                  ? "border border-red-500 bg-red-50 text-red-600"
                  : "border-none bg-gray-50 cursor-default"
                }`}
              placeholder={isEditing ? `Enter ${label}` : ""}
            />
          </div>
        ))}
      </div>

      <div className="mt-8 space-y-3 pt-6 border-t border-gray-100">
        <label className="block text-xs font-medium text-gray-700">
          Cover Image (Displayed on Builder List)
        </label>
        <div className="flex items-center gap-4">
          {profile?.builder?.cover_image ? (
            <img
              src={profile.builder.cover_image}
              alt="Cover preview"
              className="h-20 w-32 object-cover rounded-md border shadow-sm"
            />
          ) : (
            <div className="h-20 w-32 bg-gray-50 flex items-center justify-center rounded-md border border-dashed border-gray-300 text-gray-400 text-xs">
              No Cover
            </div>
          )}
          <div>
            <input
              type="file"
              id="cover-image-upload"
              className="hidden"
              accept="image/jpeg,image/png,image/webp,image/jpg"
              onChange={handleCoverImageChange}
              disabled={isUpdating}
            />
            <button
              onClick={() => document.getElementById("cover-image-upload").click()}
              className="px-4 py-2 bg-white border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-50"
              disabled={isUpdating}
            >
              {isUpdating ? "Uploading..." : profile?.builder?.cover_image ? "Change Image" : "Upload Image"}
            </button>
            <p className="text-[10px] text-gray-500 mt-2">
              Max size: 5MB. Formats: JPG, PNG, WEBP.
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default SocialMedia;